import React, { FC, useEffect, useState } from "react";

import { FileRejection, FileWithPath, useDropzone } from "react-dropzone";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import { IconButton } from "@mui/material";

import { AttachmentDisabledIcon, AttachmentIcon, SendDisabledIcon, SendIcon } from "@/assets/icons";
import { BackdropWithSpinner } from "@/components/atoms";
import { compressFiles, defaultAllowedFileTypes, getReceivedErrors } from "@/components/organisms/Uploader/utils";

import File from "../File/File";
import { formSchemaConversation, formSchemaMessage } from "./ConversationSchema";
import { StyledConversationChat, StyledConversationChatInput } from "./styled";

export enum InputMode {
    ExistingConversation,
    NewConversationWithoutContext,
    NewConversationWithContext,
}

interface UploadErrors {
    totalFilesExceeded?: boolean;
    filesTotalSizeExceeded?: string[];
    filesInvalidType?: string[];
    filesInvalidSize?: string[];
}

const defaultUploadErrors: UploadErrors = {
    filesInvalidSize: [],
    filesInvalidType: [],
    filesTotalSizeExceeded: [],
    totalFilesExceeded: false,
};

const getAcceptedFilesTotalSize = (files: FileWithPath[]): number => {
    let count = 0;
    files.forEach((file: any) => {
        count += file.size;
    });
    return count;
};

const sizeLimitCalc = 1.048576;

type ConversationChatInputProps = {
    disabled: boolean;
    inputMode: InputMode;

    setTopic?: React.Dispatch<React.SetStateAction<string>>;
    setMessage?: React.Dispatch<React.SetStateAction<string>>;

    onSubmitForm: (formData: ChatInputFormType) => Promise<boolean>;
};

export type ConversationSender = {
    name: string;
};

export type ChatInputFormType = {
    title: string;
    content: string;
    files: FileWithPath[];
    sender: ConversationSender;
};

const ConversationChatInput: FC<ConversationChatInputProps> = ({
    inputMode,
    disabled = false,
    setTopic,
    setMessage,
    onSubmitForm,
}) => {
    const [isUploading, setIsUploading] = useState(false);
    const { t } = useTranslation();

    const maxNumberOfFiles = 10;
    const maxFileSize = 2 * 1000000; // Bytes;
    const maxTotalSize = 5 * 1000000; // Bytes;

    const {
        handleSubmit,
        register,
        formState: { isValid, isDirty },
        watch,
        reset,
    } = useForm<ChatInputFormType>({
        mode: "onChange",
        resolver: yupResolver(
            inputMode === InputMode.NewConversationWithoutContext ? formSchemaConversation : formSchemaMessage,
        ),
    });

    const [files, setFiles] = useState<FileWithPath[]>([]);
    const [errors, setErrors] = useState(defaultUploadErrors);
    const {
        getRootProps,
        getInputProps,
        open: openFilePicker,
    } = useDropzone({
        disabled,
        accept: defaultAllowedFileTypes,
        noClick: true,
        maxFiles: 0, // We deal with the number of files programmatically
        maxSize: maxFileSize * sizeLimitCalc,
        getFilesFromEvent: async (event) => {
            setIsUploading(true);
            return await compressFiles(event);
        },
        onDrop: (acceptedFiles: FileWithPath[], fileRejections: FileRejection[]) => {
            let newErrors = { ...defaultUploadErrors, ...getReceivedErrors(fileRejections) };

            // 1. Get files to be uploaded >> ignore duplicated files
            let filesToBeUploaded = acceptedFiles.filter((af) => !files.find((f) => f.name === af.name));

            if (filesToBeUploaded.length === 0) {
                setErrors(newErrors);
                setIsUploading(false);
                return;
            }
            setErrors(defaultUploadErrors);

            // 2. Remove extra files
            filesToBeUploaded = filesToBeUploaded.slice(0, maxNumberOfFiles - files.length);

            // 3. Get allowed files to be uploaded
            let allowedFilesToBeUploaded = [];
            let sizeCount = getAcceptedFilesTotalSize(files);
            filesToBeUploaded.forEach((ftbu: any) => {
                const realFileSize = ftbu.size / sizeLimitCalc;
                const expectedGlobalSize = sizeCount + realFileSize;

                if (maxTotalSize !== 0 && expectedGlobalSize > maxTotalSize) {
                    newErrors = {
                        ...newErrors,
                        filesTotalSizeExceeded: [...newErrors.filesTotalSizeExceeded, ftbu.name],
                    };
                } else {
                    allowedFilesToBeUploaded = [...allowedFilesToBeUploaded, ftbu];
                    setFiles([...files, ...allowedFilesToBeUploaded]);
                    sizeCount += realFileSize;
                }
            });

            // 4. Set errors
            setErrors(newErrors);
            setIsUploading(false);
        },
    });

    useEffect(() => {
        if (files.length >= maxNumberOfFiles) {
            setErrors({ ...errors, totalFilesExceeded: false });
        }
    }, [files]);

    const handleExcludeFile = async (file: FileWithPath) => {
        setFiles(files.filter((f) => f !== file));
        setErrors(defaultUploadErrors);
    };

    const hasFileErrors =
        errors.filesInvalidSize.length > 0 ||
        errors.filesInvalidType.length > 0 ||
        errors.filesTotalSizeExceeded.length > 0;

    const hasMaximumFiles = files.length > 0 && files.length === maxNumberOfFiles;

    const title = watch("title");
    const content = watch("content");

    useEffect(() => {
        if (setTopic != null) {
            setTopic(title);
        }
    }, [title]);
    useEffect(() => {
        if (setMessage != null) {
            setMessage(content);
        }
    }, [content]);

    const onSubmit = async (data: ChatInputFormType) => {
        onSubmitForm({ files, ...data }).then((success) => {
            if (success) {
                reset();
                setFiles([]);
                setErrors(defaultUploadErrors);
            }
        });
    };

    return (
        <>
            {isUploading && <BackdropWithSpinner />}
            {files.length > 0 && (
                <div id="uploaded-files" className="uploaded-files">
                    {files.map((file) => (
                        <File key={file.name} file={file} onExcludeFile={() => handleExcludeFile(file)} />
                    ))}
                </div>
            )}
            {hasFileErrors && (
                <ul className="conversation-upload-errors">
                    {errors.filesInvalidType.length > 0 && <li>{t("Conversation.Errors.InvalidFormat")}</li>}

                    {errors.filesInvalidSize.length > 0 && <li>{t("Conversation.Errors.SizeTooBig")}</li>}

                    {errors.filesTotalSizeExceeded.length > 0 && <li>{t("Conversation.Errors.TotalSizeTooBig")}</li>}
                </ul>
            )}
            <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
                <StyledConversationChat $disabled={disabled} {...getRootProps()}>
                    <input {...getInputProps()} />
                    {inputMode === InputMode.NewConversationWithoutContext && (
                        <>
                            <StyledConversationChatInput
                                {...register("title")}
                                disabled={disabled}
                                autoFocus
                                maxRows={1}
                                minRows={1}
                                data-testid="chat-subject"
                                placeholder={t("Conversation.Subject")}
                            />
                            <hr />
                        </>
                    )}
                    <StyledConversationChatInput
                        {...register("content")}
                        autoFocus={inputMode !== InputMode.NewConversationWithoutContext && !disabled}
                        disabled={disabled}
                        maxRows={4}
                        minRows={2}
                        multiline
                        data-testid="chat-textarea"
                        placeholder={t("Conversation.TypeYourMessage")}
                    />
                    <div className="d-flex align-center justify-space-between mt10">
                        <div className="conversation-attachment-icon" data-testid="chat-attachment-button">
                            {(() => {
                                if (hasMaximumFiles) {
                                    return t("FileUploader.Errors.MaxFiles");
                                }
                                if (disabled) {
                                    return (
                                        <IconButton disabled size="large">
                                            <AttachmentDisabledIcon />
                                        </IconButton>
                                    );
                                }
                                return (
                                    <IconButton onClick={openFilePicker} size="large">
                                        <AttachmentIcon />
                                    </IconButton>
                                );
                            })()}
                        </div>
                        <div data-testid="chat-send-button">
                            {disabled || !isDirty || !isValid ? (
                                <IconButton disabled type="submit" size="large">
                                    <SendDisabledIcon />
                                </IconButton>
                            ) : (
                                <IconButton type="submit" size="large">
                                    <SendIcon />
                                </IconButton>
                            )}
                        </div>
                    </div>
                </StyledConversationChat>
            </form>
        </>
    );
};

export default ConversationChatInput;
